
import './v-toggles.css'

class VToggles extends HTMLElement {
  // Private fields
  #targetSelector = null
  #targetContainer = null
  #boundHandleChange = null
  #announcementEl = null

  constructor() {
    super()
    // Bind the handler once for cleanup
    this.#boundHandleChange = this.#handleChange.bind(this)
  }

  connectedCallback() {
    // Get the target selector from data-target attribute
    this.#targetSelector = this.dataset.target

    if (!this.#targetSelector) {
      console.warn('v-toggles: No data-target attribute specified')
      return
    }

    // Find the target container
    this.#targetContainer = document.querySelector(this.#targetSelector)

    if (!this.#targetContainer) {
      console.warn(`v-toggles: Target container not found: ${this.#targetSelector}`)
      return
    }

    // Create announcement element inside the component itself
    this.#announcementEl = document.createElement('div')
    this.#announcementEl.className = 'v:sr-only'
    this.#announcementEl.setAttribute('role', 'status')
    this.#announcementEl.setAttribute('aria-live', 'polite')
    this.#announcementEl.setAttribute('aria-atomic', 'true')
    this.appendChild(this.#announcementEl)

    // Use event delegation - single listener on the parent element
    this.addEventListener('change', this.#boundHandleChange)

    // Apply initial filter after DOM is ready (next frame)
    requestAnimationFrame(() => {
      const checkedRadio = this.querySelector('input[type="radio"]:checked')
      if (checkedRadio) {
        this.#filterElements(checkedRadio.value)
      }
    })
    this.hidden = false
  }

  disconnectedCallback() {
    // Clean up the single delegated event listener
    this.removeEventListener('change', this.#boundHandleChange)

    // Clean up announcement element
    if (this.#announcementEl) {
      this.#announcementEl.remove()
      this.#announcementEl = null
    }
  }

  setValue(value) {
    const radio = this.querySelector(`input[type="radio"][value="${value}"]`)
    if (radio) {
      radio.checked = true
      this.#filterElements(value)
      return true
    }
    return false
  }

  // Private methods
  #handleChange(event) {
    // Check if the event target is a radio button
    if (event.target.matches('input[type="radio"]')) {
      this.#filterElements(event.target.value)
    }
  }

  #filterElements(filterValue) {
    if (!this.#targetContainer) return

    // Get all elements with data-toggle-type
    const elements = this.#targetContainer.querySelectorAll('[data-toggle-type]')
    let visibleCount = 0

    elements.forEach(element => {
      const toggleType = element.dataset.toggleType

      // Special handling for 'all' filter - show everything
      // Support both exact match (backward compat) and delimiter pattern (|value|)
      const matches = filterValue === 'all'
        || toggleType === filterValue
        || toggleType.includes('|' + filterValue + '|')

      if (matches) {
        element.removeAttribute('hidden')
        visibleCount++
      } else {
        element.setAttribute('hidden', '')
      }
    })

    // Update announcement using template from data attribute
    if (this.#announcementEl) {
      const announcementTemplate = this.dataset.announcementTemplate

      if (announcementTemplate) {
        // Replace template variables
        const announcement = announcementTemplate
          .replace('{{ count }}', visibleCount)
          .replace('{{ filter_value }}', filterValue === 'all' ? 'all' : filterValue.toLowerCase())

        this.#announcementEl.textContent = announcement
      } else {
        // Fallback if no template provided
        const filterLabel = filterValue === 'all' ? 'all' : filterValue.toLowerCase()
        this.#announcementEl.textContent = `Showing ${visibleCount} ${filterLabel} products`
      }
    }

    // Dispatch custom event for other components to listen to
    this.dispatchEvent(new CustomEvent('toggle-change', {
      detail: { value: filterValue, count: visibleCount },
      bubbles: true
    }))
  }
}

// Register the custom element
if (!customElements.get('v-toggles')) {
  customElements.define('v-toggles', VToggles)
}

export default VToggles